import { z } from 'zod';
import { WORKFORCE_GROUPS, WORKFORCE_GROUP_LEGACY_VALUES } from './workforceGroup.js';
// Leaf modules only (zod and nothing else): `schemas/flaggedScanQuery.schema.ts`
// imports this file, so a schema that reaches back into the schema barrel
// would close a cycle a browser evaluates in the wrong order.
import { PersonNowSchema } from '../schemas/personNow.schema.js';
import { WorksiteTypeSchema } from '../schemas/worksite.schema.js';
import { CorrectionTypeSchema } from '../schemas/correctionType.schema.js';

/* ===========================================================================
 * THE FILTER REGISTRY — one vocabulary for every list in the app.
 *
 * Before it, each screen wrote its own filter: its own URL key, its own value
 * spellings and its own predicate. The workforce group alone travelled as
 * `scope=SITE_WORKER`, `group=SITE_WORKER`, `group=labour`, `workforce=workers`
 * and `workforceType=SITE_WORKER` on five screens, and two of them offered no
 * Remote at all (the gap list in the action-bar plan, §2).
 *
 * An entry here is the WIRE half of a filter — its key, its URL key, the zod
 * that says which values are real, the older spellings that still open it,
 * and the query parameter it travels to the server as. The SCREEN half (its
 * label, its control and where its options come from) is
 * `apps/web/src/filters/`. A screen lists which keys it offers; it never
 * writes a key, a value set or a rule of its own.
 *
 * LEGACY SPELLINGS ARE READ, NEVER WRITTEN. An old link (`?scope=SITE_WORKER`,
 * `?group=labour`) opens the same filter, and the next write replaces it with
 * the canonical key and value — so a bookmark keeps working and the address
 * converges on one spelling.
 *
 * A VALUE THE SCHEMA REFUSES READS AS ABSENT. A tampered or stale address
 * shows the unfiltered list rather than a filter nobody can see or clear.
 * ======================================================================== */

/** A register id (cuid / uuid / seeded slug) — never a free-text value. */
const RegisterId = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/);

/** A company-time calendar day (docs/time-and-dates.md): `YYYY-MM-DD`, nothing else. */
const CompanyDay = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/);

/** A company-time calendar month: `YYYY-MM`. */
const CompanyMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);

export interface FilterDef {
  /** The registry key a screen names. */
  key: FilterKey;
  /** The ONE URL key it is written under. */
  urlKey: string;
  /** Older URL keys that still open it. Read, never written. */
  legacyUrlKeys?: readonly string[];
  /** Which values are real. Anything else reads as absent. */
  schema: z.ZodType<string>;
  /** Older spellings of a value, mapped to the canonical one. */
  legacyValues?: Readonly<Record<string, string>>;
  /**
   * The query parameter the filter travels to the server as. Shared by every
   * endpoint that takes it; an endpoint that does not take it yet is a gap,
   * not a different name.
   */
  serverParam: string;
  /**
   * Where its options come from (design: "option source rule"):
   *   closed    — a fixed set from the schema;
   *   register  — the tenant's register (every site, every department), plus
   *               any chosen value no longer on it; never "what is on the page";
   *   text      — free text (the search box);
   *   toggle    — on or absent;
   *   day       — a company-time calendar day (or month).
   */
  source: 'closed' | 'register' | 'text' | 'toggle' | 'day';
}

export const FILTER_KEYS = [
  'q',
  'group',
  'site',
  'at',
  'camp',
  'dept',
  'position',
  'company',
  'payroll',
  'method',
  'flagged',
  'status',
  'day',
  'from',
  'to',
  'shape',
  'hours',
  'now',
  'siteType',
  'noLocation',
  'occupancy',
  'siteShow',
  'sites',
  'cantGo',
  'supplier',
  'nationality',
  'enrollment',
  'leaveType',
  'month',
  'correctionType',
  'raisedFrom',
  'period',
  'emirate',
  'deviceKind',
  'heldReason',
  'attention',
  'range',
  'tablet',
  'verdict',
] as const;
export type FilterKey = (typeof FILTER_KEYS)[number];

/** The scan queue's two shapes a bulk approval makes worse (flaggedScanMarks.ts). */
export const SCAN_SHAPES = ['repeat', 'double'] as const;
export type ScanShape = (typeof SCAN_SHAPES)[number];

/** Whether a waiting scan is already in its day's hours (`countsTowardRollup`). */
export const SCAN_HOURS = ['counted', 'notCounted'] as const;
export type ScanHours = (typeof SCAN_HOURS)[number];

/** A camp's beds against its capacity (the Overview's Camps board). */
export const OCCUPANCY_STATES = ['over', 'within'] as const;
export type OccupancyState = (typeof OCCUPANCY_STATES)[number];

/** Dispatch: the site lanes drawn — those holding someone, or those nobody was sent to. `all` is the default, never written. */
export const SITE_SHOWS = ['staffed', 'empty'] as const;
export type SiteShowState = (typeof SITE_SHOWS)[number];

/** Corrections: who raised the request — a field device for the worker, or the person themselves. */
export const RAISED_FROM = ['field', 'self'] as const;
export type RaisedFrom = (typeof RAISED_FROM)[number];

/** Devices: why a terminal's punch is held — the two causes `TerminalPunchQuarantine` records. */
export const HELD_REASONS = ['UNKNOWN_EMPLOYEE', 'FEATURE_DISABLED'] as const;
export type HeldReason = (typeof HELD_REASONS)[number];

/** Devices › Performance: the trailing window the figures cover. `24h` is the default, never written. */
export const PERF_RANGES = ['24h', '7d'] as const;
export type PerfRange = (typeof PERF_RANGES)[number];

/** Devices › Face enrolment: whether an enrolment weakens matching. */
export const ENROLMENT_VERDICTS = ['weak', 'good'] as const;
export type EnrolmentVerdictState = (typeof ENROLMENT_VERDICTS)[number];

/** Dispatch's "Only these sites" — a comma list of register ids, at most fifty. */
const RegisterIdList = z.string().regex(/^[A-Za-z0-9_-]{1,64}(,[A-Za-z0-9_-]{1,64}){0,49}$/);

const def = (d: FilterDef): FilterDef => Object.freeze(d);

export const FILTER_REGISTRY: Readonly<Record<FilterKey, FilterDef>> = Object.freeze({
  /** The search box. Its rule is `everyWordMatches` / `everyWordIn`. */
  q: def({ key: 'q', urlKey: 'q', legacyUrlKeys: ['search'], schema: z.string().trim().min(1).max(80), serverParam: 'q', source: 'text' }),
  /** Workers · Staff · Remote — `workforceGroup.ts` is the rule. */
  group: def({
    key: 'group',
    urlKey: 'group',
    legacyUrlKeys: ['scope', 'workforce', 'workforceType'],
    schema: z.enum(WORKFORCE_GROUPS),
    legacyValues: WORKFORCE_GROUP_LEGACY_VALUES,
    serverParam: 'group',
    source: 'closed',
  }),
  /** The site a person is ASSIGNED to over the period (api/utils/assignmentOverlap.ts). */
  site: def({ key: 'site', urlKey: 'site', legacyUrlKeys: ['worksiteId'], schema: RegisterId, serverParam: 'worksiteId', source: 'register' }),
  /**
   * "Punched at" — where a PUNCH happened: People's last movement of the day,
   * the scan queue's scan. A different fact from `site` (where a person is
   * assigned), so a different key and word: "Site" never means a punch
   * location (action-bar plan §2, item 3). The same register of options.
   * `ws` is People's older key; the scan queue's older `site` is read by that
   * screen alone (a legacy key may not be another filter's canonical one).
   */
  at: def({ key: 'at', urlKey: 'at', legacyUrlKeys: ['ws'], schema: RegisterId, serverParam: 'worksiteId', source: 'register' }),
  camp: def({ key: 'camp', urlKey: 'camp', legacyUrlKeys: ['campId'], schema: RegisterId, serverParam: 'campId', source: 'register' }),
  dept: def({ key: 'dept', urlKey: 'dept', legacyUrlKeys: ['departmentId'], schema: RegisterId, serverParam: 'departmentId', source: 'register' }),
  /** "Position" everywhere (owner D2) — by designation id, never by title. */
  position: def({ key: 'position', urlKey: 'position', legacyUrlKeys: ['designationId'], schema: RegisterId, serverParam: 'designationId', source: 'register' }),
  /** "Company" everywhere (owner D3) — the legal entity. */
  company: def({ key: 'company', urlKey: 'company', legacyUrlKeys: ['legalEntityId'], schema: RegisterId, serverParam: 'legalEntityId', source: 'register' }),
  payroll: def({ key: 'payroll', urlKey: 'payroll', legacyUrlKeys: ['erpPayrollNumber'], schema: z.string().trim().min(1).max(40), serverParam: 'erpPayrollNumber', source: 'register' }),
  method: def({ key: 'method', urlKey: 'method', schema: z.string().regex(/^[A-Z_]{2,40}$/), serverParam: 'method', source: 'closed' }),
  flagged: def({ key: 'flagged', urlKey: 'flagged', schema: z.enum(['1']), legacyValues: Object.freeze({ true: '1' }), serverParam: 'flagged', source: 'toggle' }),
  /**
   * Per entity — each screen supplies its own closed set; the key and the word
   * are shared. The roster's values carry an ERP code (`code:01`), a
   * code-less status (`nocode:ACTIVE`) or an any-of list (`ON_LEAVE,RESIGNED`)
   * — `rosterStatusFilter.ts` — so digits, `:` and `,` are real; anything
   * else still reads as absent.
   */
  status: def({ key: 'status', urlKey: 'status', schema: z.string().regex(/^[A-Za-z0-9_:,.-]{1,120}$/), serverParam: 'status', source: 'closed' }),
  day: def({ key: 'day', urlKey: 'day', legacyUrlKeys: ['date'], schema: CompanyDay, serverParam: 'date', source: 'day' }),
  from: def({ key: 'from', urlKey: 'from', schema: CompanyDay, serverParam: 'from', source: 'day' }),
  to: def({ key: 'to', urlKey: 'to', schema: CompanyDay, serverParam: 'to', source: 'day' }),
  /** Scan approvals: a repeat, or a day that does not add up. */
  shape: def({ key: 'shape', urlKey: 'shape', schema: z.enum(SCAN_SHAPES), serverParam: 'shape', source: 'closed' }),
  /** Scan approvals: already in the day's hours, or not. */
  hours: def({ key: 'hours', urlKey: 'hours', schema: z.enum(SCAN_HOURS), serverParam: 'hours', source: 'closed' }),
  /**
   * People: where a person is NOW, off their last movement (`personNow.ts`).
   * `onSite=true` — the On-site tile's older address — still opens On site:
   * the Overview reads `onSite` as a screen alias of this key, and `true` is
   * that alias's one value.
   */
  now: def({ key: 'now', urlKey: 'now', schema: PersonNowSchema, legacyValues: Object.freeze({ true: 'onSite' }), serverParam: 'now', source: 'closed' }),
  /**
   * The Overview's Sites board: one kind of site (`WorksiteType`). The popover
   * retired on 2026-10-22 wrote a comma list; its first kind is the one kept.
   */
  siteType: def({
    key: 'siteType',
    urlKey: 'siteType',
    schema: z.string().transform((v) => v.split(',')[0]!.trim()).pipe(WorksiteTypeSchema),
    serverParam: 'type',
    source: 'closed',
  }),
  /**
   * Only the sites with no coordinates — where the map's "N not on the map"
   * badge lands. `coords=missing` is the address it had before the registry.
   */
  noLocation: def({
    key: 'noLocation',
    urlKey: 'noLocation',
    legacyUrlKeys: ['coords'],
    schema: z.enum(['1']),
    legacyValues: Object.freeze({ missing: '1', true: '1' }),
    serverParam: 'noLocation',
    source: 'toggle',
  }),
  /** The Camps board: over capacity, or within it. */
  occupancy: def({ key: 'occupancy', urlKey: 'occupancy', schema: z.enum(OCCUPANCY_STATES), serverParam: 'occupancy', source: 'closed' }),
  /** Dispatch: draw the site lanes that hold someone, or only those nobody was sent to. */
  siteShow: def({ key: 'siteShow', urlKey: 'siteShow', schema: z.enum(SITE_SHOWS), serverParam: 'siteShow', source: 'closed' }),
  /**
   * Dispatch: draw only these site lanes — the sites the reader picked, by id.
   * Which lanes are DRAWN, not where anyone is assigned, so not `site`.
   */
  sites: def({ key: 'sites', urlKey: 'sites', schema: RegisterIdList, serverParam: 'worksiteIds', source: 'register' }),
  /** Dispatch: only the people in camp who can't go today (leave, expired papers). */
  cantGo: def({ key: 'cantGo', urlKey: 'cantGo', schema: z.enum(['1']), legacyValues: Object.freeze({ true: '1' }), serverParam: 'cantGo', source: 'toggle' }),
  /** The roster: the labour supplier a person comes through (`Employee.supplierId`). */
  supplier: def({ key: 'supplier', urlKey: 'supplier', legacyUrlKeys: ['supplierId'], schema: RegisterId, serverParam: 'supplierId', source: 'register' }),
  /** The roster: nationality exactly as stored — the options are the values on file. */
  nationality: def({ key: 'nationality', urlKey: 'nationality', schema: z.string().trim().min(1).max(80), serverParam: 'nationality', source: 'register' }),
  /** The roster: where a person's face enrolment stands (`EmployeeEnrollmentStatus`). */
  enrollment: def({
    key: 'enrollment',
    urlKey: 'enrollment',
    legacyUrlKeys: ['enrollmentStatus'],
    schema: z.string().regex(/^[A-Z_]{2,40}$/),
    serverParam: 'enrollmentStatus',
    source: 'closed',
  }),
  /** Leave: the leave type, by id — every type in the register, never only those in the queue. */
  leaveType: def({ key: 'leaveType', urlKey: 'leaveType', legacyUrlKeys: ['leaveTypeId'], schema: RegisterId, serverParam: 'leaveTypeId', source: 'register' }),
  /** A month-long period (the leave calendar). The current company month is the default, never written. */
  month: def({ key: 'month', urlKey: 'month', schema: CompanyMonth, serverParam: 'month', source: 'day' }),
  /** Corrections: the kind of request (`CorrectionType`), worded by `corrections.type.*`. Not `?type=`: that is the composer's prefill. */
  correctionType: def({
    key: 'correctionType',
    urlKey: 'kind',
    schema: CorrectionTypeSchema,
    serverParam: 'requestType',
    source: 'closed',
  }),
  /** Corrections: raised from a field device, or by the person themselves. */
  raisedFrom: def({
    key: 'raisedFrom',
    urlKey: 'raised',
    schema: z.enum(RAISED_FROM),
    legacyValues: Object.freeze({ FIELD: 'field', SELF: 'self' }),
    serverParam: 'source',
    source: 'closed',
  }),
  /**
   * ERP: the payroll month a batch covers. The PREVIOUS company month is the
   * default (`previousMonthOf`) and is never written — so not `month`, whose
   * default is the current one.
   */
  period: def({ key: 'period', urlKey: 'period', schema: CompanyMonth, serverParam: 'period', source: 'day' }),
  /** The worksite list: the emirate as stored — free text, so the options are the values on file (like `nationality`). */
  emirate: def({ key: 'emirate', urlKey: 'emirate', schema: z.string().trim().min(1).max(80), serverParam: 'emirate', source: 'register' }),
  /** Devices: the kind of hardware (`DeviceType`). Not `?type=`, which is another screen's prefill. */
  deviceKind: def({ key: 'deviceKind', urlKey: 'deviceKind', schema: z.string().regex(/^[A-Z_]{2,40}$/), serverParam: 'type', source: 'closed' }),
  /** Devices › Held punches: why the punch is held. */
  heldReason: def({ key: 'heldReason', urlKey: 'reason', schema: z.enum(HELD_REASONS), serverParam: 'reason', source: 'closed' }),
  /** Devices › Performance: only the tablets that need somebody (`devicePerfAttention.ts`). */
  attention: def({
    key: 'attention',
    urlKey: 'attention',
    schema: z.enum(['1']),
    legacyValues: Object.freeze({ true: '1' }),
    serverParam: 'attention',
    source: 'toggle',
  }),
  /** Devices › Performance: the last 24 hours (the default) or the last 7 days. */
  range: def({ key: 'range', urlKey: 'range', schema: z.enum(PERF_RANGES), serverParam: 'range', source: 'day' }),
  /** Devices › Face enrolment: the tablet an enrolment was captured on, by its code. */
  tablet: def({ key: 'tablet', urlKey: 'tablet', schema: RegisterId, serverParam: 'deviceCode', source: 'register' }),
  /** Devices › Face enrolment: weak or good. */
  verdict: def({ key: 'verdict', urlKey: 'verdict', schema: z.enum(ENROLMENT_VERDICTS), serverParam: 'verdict', source: 'closed' }),
});

/** The canonical value for a raw one, or undefined when it is not a real value. */
export function parseFilterValue(key: FilterKey, raw: string | null | undefined): string | undefined {
  if (raw === null || raw === undefined) return undefined;
  const entry = FILTER_REGISTRY[key];
  const value = entry.legacyValues?.[raw] ?? raw;
  const parsed = entry.schema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

/** Values by registry key; a missing key is "no filter". */
export type FilterValues = Partial<Record<FilterKey, string>>;

/** Read `keys` from an address. Canonical key first, then its legacy keys in order. */
export function readFilterParams(params: URLSearchParams, keys: readonly FilterKey[]): FilterValues {
  const out: FilterValues = {};
  for (const key of keys) {
    const entry = FILTER_REGISTRY[key];
    for (const urlKey of [entry.urlKey, ...(entry.legacyUrlKeys ?? [])]) {
      const value = parseFilterValue(key, params.get(urlKey));
      if (value !== undefined) {
        out[key] = value;
        break;
      }
    }
  }
  return out;
}

/**
 * The address with `values` written for `keys`: every spelling of each key is
 * dropped, then the canonical key is set when the value is real. Absent, empty
 * and refused values write nothing — the default never reaches the address.
 * Params that are not one of `keys` are left exactly as they were.
 */
export function writeFilterParams(
  params: URLSearchParams,
  values: FilterValues,
  keys: readonly FilterKey[]
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of keys) {
    const entry = FILTER_REGISTRY[key];
    for (const urlKey of [entry.urlKey, ...(entry.legacyUrlKeys ?? [])]) next.delete(urlKey);
    const value = parseFilterValue(key, values[key]);
    if (value !== undefined) next.set(entry.urlKey, value);
  }
  return next;
}

/** Server query params for `values`, under each filter's `serverParam`. */
export function filterServerParams(values: FilterValues, keys: readonly FilterKey[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of keys) {
    const value = parseFilterValue(key, values[key]);
    if (value !== undefined) out[FILTER_REGISTRY[key].serverParam] = value;
  }
  return out;
}
